import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import type { TarefaViva } from '@remoto/protocolo';
import { chamar, ErroApi } from '../api';
import { assinarFluxo, socketDoPainel } from '../socket/painel';
import { juntarEventos, paraItens, type EventoBruto, type ItemDeChat } from './mensagens';
import { usarMarcarVista } from './vista';
import { motorDaLinha, provedorDaConversa } from './motor-na-tela';
import { usarUsoDasSessoes } from '../uso/consultas';
import type { Pedido } from '../componentes/chat/pecas';

/**
 * A sessão como a tela a lê.
 *
 * É uma DECLARAÇÃO PRÓPRIA, e não o tipo de `SessaoSchema` — não há aresta de
 * tipo entre este arquivo e `packages/shared`. Por isso o renome do contrato
 * não apareceu aqui como erro de compilação: os dois campos abaixo passaram a
 * mentir no dia da migration e o `typecheck -w apps/web` continuou em zero, por
 * ninguém ler o valor deles. Quem mudar um nome no `SessaoSchema` muda aqui na
 * mão, e quem quiser que o compilador ajude tem de ligar os dois primeiro.
 */
export interface Sessao {
  id: string;
  idNoMotor: string;
  estado: string;
  cwd: string;
  nome: string | null;
  /** Como a máquina chama a conversa; é o rótulo do lado do VS Code. */
  nomeMaquina?: string | null;
  tituloIa: string | null;
  origem: string;
  /**
   * CLAUDE, CODEX ou OPENCODE. Ausente = CLAUDE, como no resto da casa; valor
   * desconhecido não vira nada (`motorDaLinha`, em `motor-na-tela.ts`).
   *
   * Não é enfeite: é dele que saem o catálogo de modelos oferecido e o selo.
   * Um seletor montado com o motor errado manda um nome da Anthropic para o
   * app-server da OpenAI.
   */
  motor?: string;
  modo: string | null;
  modelo: string | null;
  esforco: string | null;
  entrypoint: string | null;
  bridgeSessionId: string | null;
  /** Versão do CLI que atendeu a sessão; qual CLI, diz o motor dela. */
  versaoMotor: string | null;
  ultimoEventoEm: string | null;
  /** Quando a máquina avisou que o último turno terminou. */
  ultimoTurnoFimEm?: string | null;
  /**
   * NORMAL ou MESTRE (F13).
   *
   * Já vinha no fio e só não estava declarado: `sessoes.service.ts` monta a
   * linha com `include`, que devolve todos os escalares, e o dublê de tela já
   * carregava `funcao: 'MESTRE'`. Quem precisa é a Prosa — para saber que está
   * na mestre sem uma segunda fonte de verdade vinda por propriedade.
   */
  funcao?: 'NORMAL' | 'MESTRE';
  /**
   * De qual sessão esta nasceu numa TROCA DE MOTOR. Linhagem para o link
   * "continuada de outra conversa" do cabeçalho; nunca lida para escopo.
   */
  continuadaDeId?: string | null;
  /**
   * O botão "Compactação automática do Remoto" (R4), da CONVERSA. Ausente = a
   * API é anterior ao campo, e a coluna nasce ligada: lê-se como ligado.
   */
  compactacaoAutomatica?: boolean;
  maquina: {
    id?: string;
    nome: string;
    papel: string;
    estado: string;
    /** O relatório da sonda (ADR-0010): é ele que liga o "continuar aqui". */
    capacidades?: unknown;
    /** O bypass total (ADR-0023) valendo nesta máquina, resolvido pela API. */
    bypassTotal?: boolean;
  };
}

export interface OpcoesDaConversa {
  /**
   * Chamado imediatamente antes de o histórico entrar POR CIMA da conversa.
   *
   * É a costura entre este gancho e a rolagem, e ela existe porque as duas
   * coisas moram em lugares diferentes de propósito: os dados aqui, os `ref`
   * de DOM em quem desenha. Sem isto, `carregarAnteriores` precisaria conhecer
   * a área de rolagem — e o gancho deixaria de servir uma segunda tela.
   */
  aoCrescerParaCima?: () => void;
}

export interface Conversa {
  sessao: Sessao | undefined;
  pedidos: Pedido[];
  /** O transcript já virado em itens de tela, banco e disco juntos. */
  itens: ItemDeChat[];
  /** Os eventos crus do banco — quem conta `seq` precisa deles. */
  eventos: EventoBruto[];
  /** Subagentes vivos do stream; volátil, some quando a sessão sai do ar. */
  tarefas: TarefaViva[];
  /**
   * A resposta que está sendo escrita agora, ou string vazia.
   *
   * Volátil: não tem `seq`, não é gravada e não sobrevive a recarregar. É
   * espelho de progresso — quando a mensagem completa chega pelo transcript,
   * isto é **descartado**, porque a partir daí existe a versão de verdade.
   */
  parcial: string;
  temMais: boolean;
  temMaisNoDisco: boolean;
  erroDisco: string;
  carregando: boolean;
  carregarAnteriores: () => Promise<void>;
  /** O maior `seq` que já chegou; é o cursor de "visto até aqui". */
  maiorSeq: number;
}

/**
 * A conversa de uma sessão: carga inicial, replay por cursor, tempo real e
 * paginação para trás.
 *
 * Isto era o miolo do `PainelDaSessao`, e saiu de lá por um motivo estreito: a
 * Prosa desenha a mesma conversa de outro jeito, e **duas cópias deste código
 * produziriam divergência silenciosa**. O defeito daqui não é uma tela torta,
 * que se vê — é uma mensagem que não chega, sem erro nenhum, em uma das duas
 * telas. Por isso replay, cursor e paginação ficam num lugar só, e
 * `prosa/arquitetura.spec.ts` recusa quem tentar reimplementá-los do lado de lá.
 *
 * O que NÃO mora aqui, de propósito: rascunho do compositor, fila de envio,
 * rolagem e "visto até aqui". Isso é de quem desenha, e cada tela resolve à
 * sua maneira — a Prosa não tem barra de rolagem com âncora, por exemplo.
 */
export function usarConversa(id: string, opcoes: OpcoesDaConversa = {}): Conversa {
  const cliente = useQueryClient();
  const [eventos, setEventos] = useState<EventoBruto[]>([]);
  const [tarefas, setTarefas] = useState<TarefaViva[]>([]);
  const [parcial, setParcial] = useState('');
  const [temMais, setTemMais] = useState(false);
  // Eventos lidos direto do disco da máquina, que nunca passaram pelo banco.
  const [doDisco, setDoDisco] = useState<EventoBruto[]>([]);
  const [offsetDisco, setOffsetDisco] = useState<number | undefined>(undefined);
  const [temMaisNoDisco, setTemMaisNoDisco] = useState(true);
  const [erroDisco, setErroDisco] = useState('');
  const [carregando, setCarregando] = useState(false);
  /** Guarda contra disparar a mesma busca a cada quadro de rolagem. */
  const buscando = useRef(false);
  /** Por onde a assinatura desta conversa já passou, para reassinar sem repetir. */
  const cursor = useRef(0);

  // O aviso de "vai crescer para cima" vai por `ref`: a função chega nova a
  // cada render, e `carregarAnteriores` não pode capturar a de ontem.
  const aoCrescerParaCima = useRef(opcoes.aoCrescerParaCima);
  aoCrescerParaCima.current = opcoes.aoCrescerParaCima;

  const { data: sessao } = useQuery({
    queryKey: ['sessao', id],
    queryFn: () => chamar<Sessao>(`${ROTAS.sessoes}/${id}`),
    refetchInterval: 15_000,
  });

  const { data: pedidosDaApi = [] } = useQuery({
    queryKey: ['permissoes', id],
    // Só os pedidos DESTA sessão: sem o filtro, o cartão de qualquer conversa
    // aparecia — e era respondível — em todas. O tempo real chega pelo socket
    // (permissao.atualizada); a sondagem virou rede de segurança.
    queryFn: () => chamar<Pedido[]>(`${ROTAS.permissoes}?estado=PENDENTE&sessaoId=${id}`),
    refetchInterval: 60_000,
  });

  /*
    O motor do pedido é o da SESSÃO, e a lista de pedidos da API não o traz.
    Sem isto todo pedido chegava ao cartão sem motor — e sem motor ele é lido
    como Claude: o pedido do Codex saía com o nome de um método JSON-RPC, e o
    do OpenCode saía como JSON cru, sem o caminho e sem o diff. Os pedidos
    daqui são todos desta sessão (o filtro acima), então o motor dela é o
    deles. O que a API mandar vence.
  */
  const motorDaSessao = sessao?.motor;
  const pedidos = useMemo(
    () =>
      motorDaSessao === undefined
        ? pedidosDaApi
        : pedidosDaApi.map((p) => (p.motor === undefined ? { ...p, motor: motorDaSessao } : p)),
    [pedidosDaApi, motorDaSessao],
  );

  /**
   * Carga inicial pelo REST e assinatura com o cursor do que já chegou: o
   * replay entrega só o intervalo que falta, e o que acontecer depois vem
   * pela sala, na mesma numeração.
   */
  useEffect(() => {
    let vivo = true;
    const socket = socketDoPainel();
    cursor.current = 0;

    // Trocar de sessão no painel não pode deixar a conversa antiga na tela.
    setEventos([]);
    setDoDisco([]);
    setOffsetDisco(undefined);
    setTemMaisNoDisco(true);
    setErroDisco('');
    setParcial('');

    const aoChegar = (evento: EventoBruto & { fluxo?: string }): void => {
      if (evento.fluxo && evento.fluxo !== 'sessao:' + id) return;
      if (evento.seq > cursor.current) cursor.current = evento.seq;
      setEventos((atuais) =>
        atuais.some((e) => e.seq === evento.seq)
          ? atuais
          : [...atuais, evento].sort((a, b) => a.seq - b.seq),
      );
    };

    // Pedido de permissão em tempo real: o cartão aparece na hora, não no
    // próximo giro da sondagem. A carga completa vem pela sala da sessão.
    const aoMudarPermissao = (pedido: { sessaoId?: string }): void => {
      if (pedido.sessaoId && pedido.sessaoId !== id) return;
      void cliente.invalidateQueries({ queryKey: ['permissoes', id] });
      void cliente.invalidateQueries({ queryKey: ['permissoes-pendentes'] });
    };
    socket.on('permissao.atualizada', aoMudarPermissao);

    // Subagentes vivos: volátil, não passa por query — é só o que o stream
    // do SDK contou, e some quando a sessão sai do ar.
    const aoMudarTarefas = (dados: { sessaoId?: string; tarefas?: TarefaViva[] }): void => {
      if (dados.sessaoId && dados.sessaoId !== id) return;
      setTarefas(dados.tarefas ?? []);
    };
    socket.on('sessao.tarefas', aoMudarTarefas);

    /**
     * Pedaço da resposta: acumula enquanto ela é escrita.
     *
     * Nada aqui é inventado — se nenhum pedaço chegou, `parcial` fica vazio e
     * quem desenha mostra que está pensando. Nunca um palpite de texto.
     */
    const aoParcial = (dados: { sessaoId?: string; texto?: string }): void => {
      if (dados.sessaoId && dados.sessaoId !== id) return;
      if (!dados.texto) return;
      setParcial((atual) => atual + dados.texto);
    };
    socket.on('sessao.parcial', aoParcial);

    let cancelarAssinatura: (() => void) | null = null;

    void chamar<{ itens: EventoBruto[]; temMais: boolean }>(
      `${ROTAS.sessoes}/${id}/eventos?limite=200`,
    ).then((pagina) => {
      if (!vivo) return;
      setEventos(pagina.itens);
      setTemMais(pagina.temMais);
      cursor.current = pagina.itens.at(-1)?.seq ?? 0;
      socket.on('sessao.evento', aoChegar);
      // O cursor vai por referência: numa reconexão o replay precisa continuar
      // do último evento que chegou, não do que existia quando a tela abriu.
      cancelarAssinatura = assinarFluxo('sessao:' + id, () => cursor.current);
    });

    return () => {
      vivo = false;
      socket.off('sessao.evento', aoChegar);
      socket.off('permissao.atualizada', aoMudarPermissao);
      socket.off('sessao.tarefas', aoMudarTarefas);
      socket.off('sessao.parcial', aoParcial);
      cancelarAssinatura?.();
    };
  }, [id, cliente]);

  // Estar com esta conversa aberta e a aba à frente já é ter visto. O gancho
  // escuta o fim de turno vivo em vez de esperar o giro desta consulta.
  usarMarcarVista(id, sessao?.ultimoTurnoFimEm);

  /*
    A origem da sessão entra na tradução porque é ela que nomeia o que não tem
    autor: conversa `OBSERVADA` é do editor, e ninguém no caminho dela soube o
    nome de quem digitou. O que o painel mandou para essa mesma conversa chega
    com autor de verdade e não é tocado por esta derivação.
  */
  const doEditor = sessao?.origem === 'OBSERVADA';
  // O modelo da sessão mede o turno com imagem: respondeu outro, foi o de
  // visão da frota (R8), e a linha do fim do turno diz qual.
  const modeloDaSessao = sessao?.modelo ?? null;
  /*
    O provedor de modelos (F2) diz o NOME do custo do turno — o cobrado no
    OpenRouter, o equivalente de tabela no NanoGPT (D45) —, e o uso da sessão
    diz se o modelo dela está na assinatura. É a MESMA consulta do cabeçalho
    (mesma chave), e não uma requisição a mais.
  */
  const provedorOpencode = provedorDaConversa(motorDaLinha(sessao?.motor), modeloDaSessao);
  const assinaturaDaSessao = usarUsoDasSessoes(id ? [id] : [])[id]?.assinatura === true;
  const itens = useMemo(
    () =>
      paraItens(juntarEventos(doDisco, eventos), {
        doEditor,
        modeloDaSessao,
        provedorOpencode,
        assinaturaDaSessao,
      }),
    [doDisco, eventos, doEditor, modeloDaSessao, provedorOpencode, assinaturaDaSessao],
  );

  /**
   * Chegou resposta pelo transcript: o espelho é descartado.
   *
   * A reconciliação é por CHEGADA de evento novo, não por casar texto. Casar
   * texto foi o que quebrou a fila do compositor quando a moldura entrou — o
   * que volta nunca é byte a byte o que saiu. Aqui basta o fato: veio
   * `assistant` do transcript, então a versão de verdade existe e o provisório
   * perdeu a função.
   */
  const ultimoSeq = eventos.at(-1)?.seq ?? 0;
  useEffect(() => {
    setParcial('');
  }, [ultimoSeq]);

  /**
   * Primeiro o que está no banco; quando ele acaba, o resto vem do disco da
   * própria máquina. O histórico antigo não é copiado para cá — inclusive
   * porque existe transcript de mais de um gigabyte — então a máquina precisa
   * estar ligada para essa parte.
   */
  async function carregarAnteriores(): Promise<void> {
    if (buscando.current) return;
    buscando.current = true;
    setCarregando(true);
    setErroDisco('');
    // Antes de o conteúdo crescer para cima: sem esta marca, a leitura salta
    // para outro trecho e a pessoa perde o lugar onde estava.
    aoCrescerParaCima.current?.();
    try {
      const menor = eventos[0]?.seq;
      if (temMais && menor) {
        const pagina = await chamar<{ itens: EventoBruto[]; temMais: boolean }>(
          `${ROTAS.sessoes}/${id}/eventos?limite=200&antesDe=${menor}`,
        );
        setEventos((atuais) => [...pagina.itens, ...atuais]);
        setTemMais(pagina.temMais);
        return;
      }

      const consulta = offsetDisco === undefined ? '' : `?ateOffset=${offsetDisco}`;
      const trecho = await chamar<{
        eventos: EventoBruto[];
        inicioOffset: number;
        temMais: boolean;
      }>(`${ROTAS.sessoes}/${id}/transcript${consulta}`);

      // Sem `seq` no disco: a chave vem da posição, decrescente, para não
      // colidir com o que veio do banco.
      const base = -(doDisco.length + 1);
      setDoDisco((atuais) => [
        ...trecho.eventos.map((e, i) => ({ ...e, seq: base - (trecho.eventos.length - i) })),
        ...atuais,
      ]);
      setOffsetDisco(trecho.inicioOffset);
      setTemMaisNoDisco(trecho.temMais);
    } catch (erro) {
      const codigo = erro instanceof ErroApi ? erro.codigo : '';
      setErroDisco(
        codigo === 'vps_offline'
          ? 'O histórico mais antigo fica no computador desta sessão, e ele está desligado.'
          : codigo === 'nao_encontrado'
            ? 'Esta sessão ainda não gravou nada em disco.'
            : 'Não foi possível ler o histórico nesta máquina.',
      );
      setTemMaisNoDisco(false);
    } finally {
      buscando.current = false;
      setCarregando(false);
    }
  }

  return {
    sessao,
    pedidos,
    itens,
    eventos,
    tarefas,
    temMais,
    temMaisNoDisco,
    erroDisco,
    carregando,
    carregarAnteriores,
    parcial,
    maiorSeq: ultimoSeq,
  };
}
